import React, { FC, useState } from "react";
import GallerySlider from "components/GallerySlider/GallerySlider";
import { currencySymbol } from "shared/CurrencySymbol";
import { Link } from "react-router-dom";
import { PropertyToEdit } from "redux/reducers/Properties/PropertiesSliceTypes";

export interface SimilarCardProps {
  className?: string;
  similarProperties: PropertyToEdit;
}

const SimilarCard: FC<SimilarCardProps> = ({
  className = "",
  similarProperties,
}) => {
  const href = `/listing-stay-detail/${similarProperties?.slug}`;

  const address = [similarProperties?.city, similarProperties?.country]
    .filter(Boolean)
    .join(", ");

  // ✅ FIX: Explicitly guard against undefined, null, or empty string images
  // The same bug as StayCardV5 — passing undefined/[] to GallerySlider
  // causes it to fall back to demo images internally.
  const safeImages = Array.isArray(similarProperties?.place_images)
    ? similarProperties.place_images
        .map((img: any) => String(img))
        .filter((img) => img.trim().length > 0)
    : [];

  return (
    <div
      className={`nc-SimilarCard group relative bg-white dark:bg-neutral-900 border border-neutral-100 dark:border-neutral-800 rounded-2xl overflow-hidden hover:shadow-[0_8px_30px_rgba(245,105,72,0.12)] hover:-translate-y-0.5 transition-all duration-200 ${className}`}
      data-nc-id="SimilarCard"
    >
      {/* Gallery */}
      <div className="relative w-full">
        <GallerySlider
          uniqueID={`SimilarCard_${similarProperties?.id}`}
          ratioClass="aspect-w-4 aspect-h-3"
          galleryImgs={safeImages}
          href={href}
        />

        {/* Property type pill — top left */}
        {similarProperties?.property_type?.name && (
          <div className="absolute top-3 left-3 z-10 pointer-events-none">
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold bg-white/90 dark:bg-neutral-900/90 text-[#1A1A2E] dark:text-white backdrop-blur-sm shadow-sm border border-white/60 dark:border-neutral-700">
              {similarProperties.property_type.name}
            </span>
          </div>
        )}

        {/* Orange accent line on hover */}
        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#F56948] to-[#F56948]/30 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
      </div>

      {/* Content */}
      <Link to={href} className="block p-4">
        <div className="flex flex-col gap-2.5">

          {/* Title */}
          <h3 className="text-sm font-bold text-[#1A1A2E] dark:text-white line-clamp-2 leading-snug tracking-tight group-hover:text-[#F56948] transition-colors duration-150">
            {similarProperties?.title || "Property"}
          </h3>

          {/* Location */}
          {address && (
            <div className="flex items-start gap-1.5">
              <svg className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-[#F56948]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <p className="text-xs text-neutral-400 dark:text-neutral-500 line-clamp-1">
                {address}
              </p>
            </div>
          )}

          {/* Divider */}
          <div className="h-px bg-neutral-100 dark:bg-neutral-800" />

          {/* Price + beds/baths row */}
          <div className="flex items-end justify-between">

            {/* Price */}
            <div className="flex items-baseline gap-1">
              <span className="text-xs text-neutral-400 font-normal mr-0.5">From</span>
              <span className="text-base font-extrabold text-[#1A1A2E] dark:text-white tracking-tight">
                {currencySymbol()}
                {similarProperties?.price_per_month
                  ? Number(similarProperties.price_per_month).toLocaleString(undefined, {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })
                  : "N/A"}
              </span>
              <span className="text-xs text-neutral-400 font-normal">/mo</span>
            </div>

            {/* Beds & Baths chips */}
            <div className="flex items-center gap-1.5">
              {similarProperties?.total_bedrooms && similarProperties.total_bedrooms > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-neutral-100 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400">
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M3 10V6a1 1 0 011-1h16a1 1 0 011 1v4M3 10h18M3 10v8m18-8v8M3 18h18" />
                  </svg>
                  {similarProperties.total_bedrooms}
                </span>
              )}
              {similarProperties?.total_bathrooms && similarProperties.total_bathrooms > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-neutral-100 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400">
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M4 12h16M4 12a4 4 0 01-4-4V6h24v2a4 4 0 01-4 4M4 12v6a2 2 0 002 2h12a2 2 0 002-2v-6" />
                  </svg>
                  {similarProperties.total_bathrooms}
                </span>
              )}
            </div>
          </div>

        </div>
      </Link>
    </div>
  );
};

export default SimilarCard;